import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../../contexts/auth";
import { useOrganizationList } from "../../hooks/useOrganizationList";
import { colors } from "../../theme/colors";

function displayName(org) {
  return org.org_name || `Organization ${org.org_id}`;
}

function locationLabel(count) {
  if (!count) return "No locations yet";
  return count === 1 ? "1 location" : `${count} locations`;
}

/**
 * Shown to a JDTech super admin after login, instead of the app, until an
 * organization is chosen.
 */
export default function OrganizationPicker() {
  const navigate = useNavigate();
  const { selectOrganization, signOut, signedInUser } = useAuth();
  const { organizations, loading, error, reload } = useOrganizationList();
  const [search, setSearch] = useState("");

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return organizations;
    return organizations.filter((org) =>
      displayName(org).toLowerCase().includes(needle)
    );
  }, [organizations, search]);

  const handleSelect = (org) => {
    selectOrganization(org);
    navigate("/");
  };

  return (
    // Same full-screen look as the login page.
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: colors.black,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        boxSizing: "border-box",
        overflow: "auto",
      }}
    >
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL}/assets/logo_divider.png`}
        alt="QUE"
        sx={{ width: "100%", maxWidth: 220, height: "auto", mb: 0.25 }}
      />
      <Typography
        sx={{
          color: colors.gold,
          fontSize: "0.7rem",
          letterSpacing: "0.35em",
          fontWeight: 500,
          textTransform: "uppercase",
          mb: 4,
        }}
      >
        The Salon Platform
      </Typography>

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 480,
          p: 3,
          borderRadius: "8px",
          boxSizing: "border-box",
          // colors.text is near-black and disappears on the dark paper surface.
          color: "text.primary",
          bgcolor: "background.paper",
        }}
      >
        {/* A div, not an h1: the DevExtreme theme styles h1 globally. */}
        <Typography
          component="div"
          role="heading"
          aria-level={1}
          sx={{
            fontSize: "1.25rem",
            fontWeight: 700,
            lineHeight: 1.3,
            mb: 0.5,
            color: "text.primary",
          }}
        >
          Choose an organization
        </Typography>
        <Typography
          sx={{ fontSize: "0.85rem", color: "text.secondary", mb: 2 }}
        >
          Signed in as {signedInUser?.email}. You will work inside the
          organization you choose, and you can switch later from the header.
        </Typography>

        <TextField
          label="Search organizations"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          fullWidth
          autoFocus
          sx={{
            mb: 1.5,
            "& .MuiInputBase-input": { color: "text.primary" },
            "& .MuiInputLabel-root": { color: "text.secondary" },
            "& .MuiOutlinedInput-root.Mui-focused fieldset": {
              borderColor: colors.gold,
            },
            "& .MuiInputLabel-root.Mui-focused": { color: colors.goldHover },
          }}
        />

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: colors.gold }} />
          </Box>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={reload}>
                Try again
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!loading && !error && organizations.length === 0 && (
          <Typography sx={{ fontSize: "0.9rem", py: 2, color: "text.primary" }}>
            No organizations found.
          </Typography>
        )}

        {!loading &&
          !error &&
          organizations.length > 0 &&
          visible.length === 0 && (
            <Typography sx={{ fontSize: "0.9rem", py: 2, color: "text.primary" }}>
              No organization matches your search.
            </Typography>
          )}

        {!loading && !error && visible.length > 0 && (
          <List
            disablePadding
            sx={{
              maxHeight: "min(360px, 45vh)",
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "6px",
            }}
          >
            {visible.map((org, index) => (
              <ListItemButton
                key={org.org_id}
                divider={index < visible.length - 1}
                onClick={() => handleSelect(org)}
                sx={{ py: 0.75 }}
              >
                <ListItemText
                  primary={displayName(org)}
                  secondary={locationLabel(org.location_count)}
                  primaryTypographyProps={{
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                  secondaryTypographyProps={{
                    fontSize: "0.8rem",
                    color: "text.secondary",
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            onClick={signOut}
            sx={{ color: colors.goldHover, fontWeight: 700 }}
          >
            Sign out
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
